import { useTranslation } from 'react-i18next';
import { PieChart, Pie, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { HEALTH_COLORS } from '../health/index.js';
import type { HealthGrade } from '../../../types/project.js';

/** 健康度分布カラーマップ（keyベース） */
/** @graph-connects none */
const HEALTH_PIE_COLOR_MAP: Record<string, string> = {
  healthy: '#16a34a',
  warning: '#f97316',
  danger: '#ef4444',
  noData: '#6b7280',
};

/**
 * 健康度分布 Pie Chart カード
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクト健康度分布をPieChartで表示。healthy/warning/danger/noDataの件数と平均スコアを視覚化
 * @graph-connects none
 */
export function HealthPieCard({
  healthStats,
  healthGrade,
}: {
  healthStats: {
    healthy: number;
    warning: number;
    danger: number;
    noData: number;
    avgScore: number;
  };
  healthGrade: HealthGrade;
}) {
  const { t } = useTranslation();

  const healthPieData = [
    {
      key: 'healthy',
      name: t('projects.health.pie.healthy'),
      value: healthStats.healthy,
      fill: HEALTH_PIE_COLOR_MAP.healthy,
    },
    {
      key: 'warning',
      name: t('projects.health.pie.warning'),
      value: healthStats.warning,
      fill: HEALTH_PIE_COLOR_MAP.warning,
    },
    {
      key: 'danger',
      name: t('projects.health.pie.danger'),
      value: healthStats.danger,
      fill: HEALTH_PIE_COLOR_MAP.danger,
    },
    {
      key: 'noData',
      name: t('projects.health.pie.noData'),
      value: healthStats.noData,
      fill: HEALTH_PIE_COLOR_MAP.noData,
    },
  ].filter((d) => d.value > 0);

  return (
    <Card className="animate-content-reveal h-full flex flex-col">
      <CardContent className="flex flex-1 items-center">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="space-y-2">
            <p className="text-sm text-[var(--color-text-muted)] mb-1">
              {t('projects.health.distribution')}
            </p>
            {[
              {
                label: t('projects.health.pie.healthy'),
                color: HEALTH_PIE_COLOR_MAP.healthy,
                value: healthStats.healthy,
              },
              {
                label: t('projects.health.pie.warning'),
                color: HEALTH_PIE_COLOR_MAP.warning,
                value: healthStats.warning,
              },
              {
                label: t('projects.health.pie.danger'),
                color: HEALTH_PIE_COLOR_MAP.danger,
                value: healthStats.danger,
              },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-sm text-[var(--color-text-secondary)]">
                  {item.label}: {String(item.value)}件
                </span>
              </div>
            ))}
            <div className="pt-2 border-t border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('projects.health.avgScore')}
              </span>
              <span
                className="ml-2 text-lg font-bold"
                style={{ color: HEALTH_COLORS[healthGrade].bg }}
              >
                {healthGrade}
              </span>
              <span className="ml-1 text-sm text-[var(--color-text-secondary)]">
                ({String(healthStats.avgScore)}
                {t('projects.health.unit')})
              </span>
            </div>
          </div>
          <div className="w-36 h-36">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={healthPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  dataKey="value"
                  strokeWidth={0}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 50, outline: 'none' }}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)',
                  }}
                  formatter={((value: number) => [`${String(value)}件`, '']) as never}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
